import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { SearchBox, Drawer, TabPanel, Tabs } from 'tea-component';
import { useDispatch, useIntl, useSelector, getLocale } from 'umi';
import SvgIcon from '@/pages/home/components/SvgIcon';

import GameTypeFilter from './components/GameTypeFilter';
import RelatedGame from './components/RelatedGame';
import AllGame from './components/AllGame';
import NewGame from './components/NewGame';
import './index.scss';

import { AllGameProps, GameSelectorProps, NewGameProps } from './types';
import { IS_GLOBAL, IS_INTLGAME_AND_NOT_TEST } from '@/pages/home/_config/config';
import { sortGames } from './_utils';

const GameSelector = (props: GameSelectorProps) => {
  const { visible, setVisible, menuWidth = 0 } = props;
  const intl = useIntl();
  const { currentGame, recentList, followList, gameList, newGame } = useSelector((state: any) => {
    const {
      currentGame,
      gameList: gameListOrigin,
      followList: followListOrigin,
      recentList: recentListOrigin,
      leftMenuWidth,
    } = state.dashboard;
    const isEN = getLocale() === 'en-US';
    // 最终显示的收藏列表
    const followList = followListOrigin?.map((game: any) => ({
      ...game,
      game_name: isEN ? game.game_name || game.game_cn_name : game.game_cn_name || game.game_name,
      game_cn_name: isEN ? game.game_cn_name : game.game_name,
    })) || [];

    let gameList = gameListOrigin;

    if (IS_GLOBAL || IS_INTLGAME_AND_NOT_TEST) {
      // 过滤子版本无权限的游戏
      gameList = gameList.map((item: any) => {
        const { game_List: subGameList = [] } = item;
        return {
          ...item,
          game_List: subGameList.filter((subItem: any) => subItem?.is_access)
        };
      });

      // 删除主版本无权限的游戏
      gameList = (gameList || []).filter((item: any) => item?.is_access || (item?.game_List?.length > 0));
    }

    // 最终显示的全部游戏
    gameList = gameList?.map((hostGame: any) => {
      const followTarget = followList?.find((followItem: any) => followItem.game_code === hostGame.game_code);
      const game_list = hostGame?.game_list?.map((game: any) => ({
        ...game,
        game_name: isEN ? game.game_name || game.game_cn_name : game.game_cn_name || game.game_name,
        game_cn_name: isEN ? game.game_cn_name : game.game_name,
      }));
      return {
        ...hostGame,
        game_list,
        game_name: isEN ? hostGame.game_name || hostGame.game_cn_name : hostGame.game_cn_name || hostGame.game_name,
        game_cn_name: isEN ? hostGame.game_cn_name : hostGame.game_name,
        isFollow: Boolean(followTarget),
        followId: followTarget?.id,
        followSort: followTarget?.sort,
      };
    }) || [];
    // 最终显示的最新浏览
    const recentList = recentListOrigin?.map((game: any) => {
      const followTarget = followList?.find((followItem: any) => followItem.game_code === game.game_code);

      return {
        ...game,
        game_name: isEN ? game.game_name || game.game_cn_name : game.game_cn_name || game.game_name,
        game_cn_name: isEN ? game.game_cn_name : game.game_name,
        isFollow: Boolean(followTarget),
        followId: followTarget?.id,
        followSort: followTarget?.sort,
      };
    }) || [];
    const newGame = gameList
      ?.map((hostGame: any) => {
        const game_List = hostGame?.game_List?.filter((game: any) => game?.is_new_game);

        return {
          ...hostGame,
          game_List,
        };
      })
      ?.filter((game: any) => game?.game_List?.length > 0 || game?.is_new_game) || [];

    return {
      currentGame,
      gameList,
      recentList,
      followList,
      newGame,
      leftMenuWidth
    };
  });
  // 是否首次进入
  const isFirst = useRef(true);
  const tabs = [
    {
      id: 'related',
      icon: 'uesr-heart',
      num: (followList?.length + recentList?.length) || 0,
      label: intl.formatMessage({ id: 'game_selector_title_game_related' }),
      component: (props: any = {}) => (
        <RelatedGame
          setGameModalVisible={props.setVisible}
          recentList={props.recentList}
          followList={props.followList}
          setActiveId={props.setActiveId}
          menuWidth={menuWidth}
        />
      ),
    },
    {
      id: 'all',
      icon: 'outline-gamepad',
      num: gameList?.length || 0,
      label: intl.formatMessage({ id: 'game_selector_title_game_all' }),
      component: (props: AllGameProps) => <AllGame gameList={props.gameList} setGameModalVisible={props.setVisible} menuWidth={menuWidth} />,
    },
    {
      id: 'new',
      icon: 'flag-m',
      num: newGame?.length || 0,
      label: intl.formatMessage({ id: 'game_selector_title_game_new' }),
      component: (props: NewGameProps) => (
        <NewGame setGameModalVisible={props.setVisible} newGame={props.newGame} setActiveId={props.setActiveId} menuWidth={menuWidth} />
      ),
    },
  ];
  const [searchValue, setSearchValue] = useState('');
  const isSearchMode = searchValue?.length > 0;
  const [activeId, setActiveId] = useState<string>(tabs[0].id);
  const dispatch = useDispatch();

  /**
   * 抽取之前逻辑作为函数，测试游戏名称/中文名/代码是否匹配搜索值
   * @param game 游戏对象，包含 game_name、game_cn_name、game_code 字段
   * @returns 是否匹配搜索值（不区分大小写，允许字符间有空格）
   */
  const testGame = useCallback((game: any) => {
    let { game_name, game_cn_name, game_code } = game || {};
    game_name = game_name?.toLowerCase();
    game_cn_name = game_cn_name?.toLowerCase();
    game_code = game_code?.toLowerCase();
    const searchVal = searchValue?.toLowerCase();
    let searchReg = '';
    // 组装正则表达式：表示每个字符后面可能存在空格
    for (const char of searchVal) {
      searchReg += `${char}\\s*`;
    }
    const regex = new RegExp(searchReg, 'gi');
    return regex?.test(game_name) || regex?.test(game_cn_name) || regex?.test(game_code);
  },[searchValue]);

  const searchGameList = useMemo(() => {
    if (!isSearchMode) return [];

    const result =  gameList?.filter((game: any) => {
      if(testGame(game)) {
        return true;
      } 
      return game?.game_List.some((subGame: any) => testGame(subGame));
    });
    // 对搜索结果进行权重排序
    return sortGames(result);
  }, [gameList, searchValue, intl.locale]);



  const handleSearch = (searchValue: string) => {
    // 搜索模式下不选中任何 tab 页
    setSearchValue(searchValue);
  };

  const handleActiveChange = (id: any) => {
    // 查询模式下禁止切换
    if (isSearchMode) return;
    isFirst.current = false;
    setActiveId(id);
  };

  useEffect(() => {
    if (!visible || !currentGame?.game_code || activeId !== 'related') return;
    // 获取最近浏览和收藏的游戏
    dispatch({
      type: 'dashboard/getRecentAndFollowViewd',
      payload: {
        game_code: currentGame?.game_code,
      },
    }).then((isShowRelated: boolean) => {
      // 首次进入且 related 下无数据则展示所有游戏 tab
      if (isFirst.current && !isShowRelated && activeId === 'related') {
        setActiveId(tabs[1].id);
      }
    });
  }, [visible, activeId, currentGame]);

  return (
    <>
      <Drawer
        className="drawer-left dtb-db-gameselector-wrap"
        style={{ left: `${menuWidth + 6}px` }}
        placement="left"
        // destroyOnClose={true}
        disableCloseIcon
        disableAnimation={false}
        outerClickClosable={true}
        showMask={false}
        visible={visible}
        onClose={() => {
          setVisible(false);
        }}
      >
        <div className="dbd-drawer-header">
          <SearchBox
            className="drawer-search"
            autoComplete="off"
            aria-autocomplete="none"
            placeholder={intl.formatMessage({ id: 'game_selector_search_text' })}
            onChange={(value) => handleSearch(value?.trim() || '')}
            onClear={() => handleSearch('')}
          />
          <Tabs
            style={{ display: searchValue ? 'none' : 'block' }}
            tabs={tabs}
            activeId={isSearchMode ? '' : activeId}
            tabBarRender={(children, tab: any) => (
              <div className="drawer-flex" onClick={() => handleActiveChange(tab.id)}>
                <SvgIcon className="drawer-flex-icon" type={tab.icon} />
                {children} ({tab.num})
              </div>
            )}
          />
        </div>
        {isSearchMode ? (
          <GameTypeFilter gameList={searchGameList} searchValue={searchValue} setGameModalVisible={setVisible} menuWidth={menuWidth}/>
        ) : (
          tabs.map((tab) =>
            activeId == tab.id ? (
              <TabPanel id={tab.id} key={tab.id}>
                {tab.component({
                  setVisible,
                  recentList,
                  followList,
                  newGame,
                  gameList,
                  setActiveId,
                })}
              </TabPanel>
            ) : (
              ''
            ),
          )
        )}
      </Drawer>
    </>
  );
};

export default GameSelector;
